// Import React and hooks for managing component state and lifecycle
import React, { useEffect, useState } from 'react';
// Import the desktop/tablet layout component
import AppLayout from './AppLayout';
// Import the mobile-specific layout component
import MobileLayout from './MobileLayout';

// Define the props (properties) that this component expects to receive
// ResponsiveLayoutProps is a TypeScript interface that describes what data the component needs
interface ResponsiveLayoutProps {
  // children: The content that will be displayed inside this layout component
  // React.ReactNode means it can be any valid React element (text, components, etc.)
  children: React.ReactNode;
}

// ResponsiveLayout is the main component that decides whether to show mobile or desktop layout
// It receives 'children' as a prop, which is the content to display inside the layout
function ResponsiveLayout({ children }: ResponsiveLayoutProps) {
  // useState creates a state variable 'isMobile' that tracks if we're on a mobile device
  // useState(false) means it starts as false (assumes desktop initially)
  // setIsMobile is the function we use to update this state
  const [isMobile, setIsMobile] = useState(false);

  // useEffect runs code when the component first loads and when dependencies change
  // This effect runs once when the component mounts (loads for the first time)
  useEffect(() => {
    // checkMobile is a function that determines if the user is on a mobile device
    const checkMobile = () => {
      // Check multiple conditions to detect mobile devices:
      // 1. window.innerWidth < 768: Screen width smaller than 768 pixels (tablet/phone)
      // 2. 'ontouchstart' in window: Device supports touch events (touchscreen)
      // 3. navigator.maxTouchPoints > 0: Device reports having touch points
      const isMobileDevice = window.innerWidth < 768 || 
        ('ontouchstart' in window) || 
        (navigator.maxTouchPoints > 0);
      // Update our state with whether this is a mobile device
      setIsMobile(isMobileDevice);
    };

    // Run the mobile check immediately when the component first loads
    checkMobile();

    // Create a function that will run every time the user resizes their browser window
    // This ensures the layout switches between mobile and desktop as the window size changes
    const handleResize = () => {
      checkMobile(); // Re-check if device should be treated as mobile
    };

    // Add an event listener to detect when the browser window is resized
    // This allows the layout to respond dynamically to size changes
    window.addEventListener('resize', handleResize);
    
    // Cleanup function: Remove the event listener when component unmounts (is removed)
    // This prevents memory leaks and ensures we don't keep listening after component is gone
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []); // Empty dependency array [] means this effect runs only once when component mounts

  // Choose which layout component to use based on whether device is mobile
  // If isMobile is true, use MobileLayout; otherwise use AppLayout (desktop)
  // This is a ternary operator: condition ? valueIfTrue : valueIfFalse
  const Layout = isMobile ? MobileLayout : AppLayout;

  // Render (display) the chosen layout component and pass the children content to it
  // JSX syntax: <Layout>{children}</Layout> means "render Layout component with children inside"
  return <Layout>{children}</Layout>;
}

// Export this component as the default export so other files can import and use it
// This makes ResponsiveLayout available to be used in other parts of the application
export default ResponsiveLayout;